import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { verifyMagicLink } from '../../services/magicLink'
import { clearAuthToken, setAuthToken } from '../../utils/authToken'

export default function PortalAuthCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const ranRef = useRef(false)
  const [errored, setErrored] = useState(false)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    const token = params.get('token')
    if (!token) {
      navigate('/portal/sign-in', { replace: true })
      return
    }

    // Manually orchestrated to guarantee the new JWT is committed to
    // localStorage *before* we navigate — otherwise the dashboard query
    // can fire under a stale token (e.g. one left over from a prior
    // staff session sharing the same origin).
    void (async () => {
      try {
        // Clear any stale token first so a failed verify can't accidentally
        // leave the user signed in as someone else.
        clearAuthToken()
        queryClient.removeQueries({ queryKey: ['currentUser'] })

        const data = await verifyMagicLink(token)
        setAuthToken(data.token)
        queryClient.setQueryData(['currentUser'], data.user)

        const returnTo = params.get('return') || '/portal/dashboard'
        navigate(returnTo, { replace: true })
      } catch {
        setErrored(true)
      }
    })()
  }, [params, navigate, queryClient])

  const verify = { isError: errored }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 text-center">
        {verify.isError ? (
          <>
            <div className="text-4xl mb-3">⚠️</div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">This link can’t be used</h1>
            <p className="text-slate-600 mb-6">
              It may have expired or already been used. Magic links last 15 minutes.
            </p>
            <Link
              to="/portal/sign-in"
              className="inline-block bg-brand-500 hover:bg-brand-600 text-white font-semibold py-2.5 px-5 rounded-lg"
            >
              Send a new link
            </Link>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  )
}
